import * as React from 'react';
import { Check, Loader2, Plus, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Checkbox } from './checkbox';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';

/**
 * Filters sit on one line above a list, one button per dimension:
 * - not set: a dashed "+ Label" button that opens a popover
 * - set:     "Label | value" plus its own × to clear just this filter
 * Changes apply at once (no Apply button); the list updates behind the popover.
 */

export interface FilterOption {
  value: string;
  label: string;
  /** Optional right-aligned count, e.g. how many items carry this value. */
  count?: number;
}

/** Past this many options the popover gets a search field. */
const SEARCH_THRESHOLD = 7;

interface FilterChipProps {
  label: string;
  /** Text for the current value; leave undefined while the filter is not set. */
  valueLabel?: string;
  onClear: () => void;
  className?: string;
}

/** The on-line trigger. Render it inside a `<Popover>`; the popover anchors to the whole chip. */
export function FilterChip({ label, valueLabel, onClear, className }: FilterChipProps) {
  const { t } = useTranslation();

  if (!valueLabel) {
    return (
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-dashed border-muted-foreground/35 bg-card px-2.5 text-[13px] font-medium text-foreground/80 transition-colors hover:border-muted-foreground/60 hover:bg-muted/60 hover:text-foreground',
            className,
          )}
        >
          <Plus className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2.25} aria-hidden />
          {label}
        </button>
      </PopoverTrigger>
    );
  }

  return (
    <PopoverAnchor asChild>
      <span
        className={cn(
          'inline-flex h-9 shrink-0 items-stretch overflow-hidden rounded-md border border-border bg-card text-[13px]',
          className,
        )}
      >
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex min-w-0 items-center gap-2 pl-2.5 pr-2 transition-colors hover:bg-muted">
            <span className="text-muted-foreground">{label}</span>
            <span aria-hidden className="h-3.5 w-px bg-border" />
            <span className="max-w-[160px] truncate font-medium text-foreground">{valueLabel}</span>
          </button>
        </PopoverTrigger>
        <button
          type="button"
          onClick={onClear}
          aria-label={t('filter_remove', { label })}
          className="inline-flex w-7 shrink-0 items-center justify-center border-l border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </span>
    </PopoverAnchor>
  );
}

interface FilterOptionListProps {
  options: FilterOption[];
  selected: string[];
  multiple: boolean;
  onToggle: (value: string) => void;
  searchable: boolean;
  searchPlaceholder?: string;
  loading?: boolean;
  emptyLabel?: string;
}

function FilterOptionList({
  options,
  selected,
  multiple,
  onToggle,
  searchable,
  searchPlaceholder,
  loading = false,
  emptyLabel,
}: FilterOptionListProps) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  const needle = query.trim().toLocaleLowerCase();
  const visible = needle ? options.filter((o) => o.label.toLocaleLowerCase().includes(needle)) : options;

  return (
    <div className="flex flex-col">
      {searchable && (
        <label className="mb-1 flex h-8 items-center gap-2 rounded-md border border-border px-2 text-muted-foreground focus-within:border-foreground/40">
          <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder ?? t('search')}
            aria-label={searchPlaceholder ?? t('search')}
            className="min-w-0 flex-1 bg-transparent text-[13px] text-foreground outline-none focus-visible:!outline-none placeholder:text-muted-foreground"
          />
        </label>
      )}
      <div
        role="listbox"
        aria-multiselectable={multiple || undefined}
        className="max-h-72 overflow-y-auto"
      >
        {loading && (
          <p className="flex items-center justify-center gap-2 py-3 text-[13px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            {t('loading')}
          </p>
        )}
        {!loading && visible.length === 0 && (
          <p className="px-2 py-3 text-center text-[13px] text-muted-foreground">
            {needle || !emptyLabel ? t('filter_no_matches') : emptyLabel}
          </p>
        )}
        {!loading &&
          visible.map((option) => {
            const isSelected = selected.includes(option.value);
            const count =
              option.count != null ? (
                <span className="text-xs tabular-nums text-muted-foreground">{option.count}</span>
              ) : null;
            return multiple ? (
              <label
                key={option.value}
                role="option"
                aria-selected={isSelected}
                className="flex h-[34px] cursor-pointer items-center gap-2.5 rounded-md px-2 text-[13.5px] text-foreground transition-colors hover:bg-muted"
              >
                <Checkbox checked={isSelected} onCheckedChange={() => onToggle(option.value)} />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {count}
              </label>
            ) : (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onToggle(option.value)}
                className={cn(
                  'flex h-[34px] w-full items-center gap-2 rounded-md px-2 text-left text-[13.5px] text-foreground transition-colors hover:bg-muted',
                  isSelected && 'font-semibold',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {count}
                {isSelected && <Check className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />}
              </button>
            );
          })}
      </div>
    </div>
  );
}

interface FilterButtonBaseProps {
  label: string;
  options: FilterOption[];
  /** Defaults to on when there are more than 7 options. */
  searchable?: boolean;
  searchPlaceholder?: string;
  loading?: boolean;
  /** Called when the popover opens or closes — e.g. to load options lazily. */
  onOpenChange?: (open: boolean) => void;
  /** Shown on the chip when the selected option is not in `options` yet. */
  valueLabel?: string;
  /** Replaces "no matches" when there are no options at all (e.g. a load error). */
  emptyLabel?: string;
  className?: string;
}

interface SingleFilterButtonProps extends FilterButtonBaseProps {
  mode?: 'single';
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

interface MultipleFilterButtonProps extends FilterButtonBaseProps {
  mode: 'multiple';
  value: string[];
  onChange: (value: string[]) => void;
}

export type FilterButtonProps = SingleFilterButtonProps | MultipleFilterButtonProps;

/** A filter over a list of options. Single choice closes on pick; multiple choice stays open. */
export function FilterButton(props: FilterButtonProps) {
  const { label, options, searchable, searchPlaceholder, loading, onOpenChange, valueLabel, emptyLabel, className } = props;
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const multiple = props.mode === 'multiple';
  const selected = props.mode === 'multiple' ? props.value : props.value ? [props.value] : [];

  const chipValue = (() => {
    if (selected.length === 0) return undefined;
    if (selected.length > 1) return t('filter_n_values', { n: selected.length });
    return options.find((o) => o.value === selected[0])?.label ?? valueLabel ?? t('filter_n_values', { n: 1 });
  })();

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const clear = () => {
    if (props.mode === 'multiple') props.onChange([]);
    else props.onChange(undefined);
  };

  const toggle = (value: string) => {
    if (props.mode === 'multiple') {
      props.onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
      return;
    }
    props.onChange(selected[0] === value ? undefined : value);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <FilterChip label={label} valueLabel={chipValue} onClear={clear} className={className} />
      <PopoverContent align="start" className="w-64 p-1.5">
        <FilterOptionList
          options={options}
          selected={selected}
          multiple={multiple}
          onToggle={toggle}
          searchable={searchable ?? options.length > SEARCH_THRESHOLD}
          searchPlaceholder={searchPlaceholder}
          loading={loading}
          emptyLabel={emptyLabel}
        />
        {multiple && selected.length > 0 && (
          <div className="-mx-1.5 -mb-1.5 mt-1.5 flex h-10 items-center justify-between border-t border-border px-3">
            <button type="button" onClick={clear} className="text-[13px] font-medium text-foreground/80 hover:text-foreground hover:underline">
              {t('clear')}
            </button>
            <span className="text-xs text-muted-foreground">{t('filter_n_selected', { n: selected.length })}</span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface FilterResetProps {
  onClick: () => void;
  className?: string;
}

/** "Tozalash" at the end of the filter line; render it only while something is set. */
export function FilterReset({ onClick, className }: FilterResetProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('h-9 shrink-0 px-1.5 text-[13px] font-medium text-foreground/80 hover:text-foreground hover:underline', className)}
    >
      {t('clear')}
    </button>
  );
}
